import React, { useState, useEffect } from "react";
import { ADD_PATIENT_MODAL_EVENT } from "../../Calendar/SelectClientModal";
import styles from "./AddPatient.module.scss";
import { useForm } from "@mantine/form";
import { useDropdownValues } from "../../../contexts/DropdownContext";
import {
  IconUser,
  IconUpload,
  IconFile,
  IconX,
  IconCalendar,
  IconCamera,
  IconInfoCircle,
} from "@tabler/icons-react";
import PrimaryButton from "../../PrimaryButton/PrimaryButton";
import {
  Modal,
  ActionIcon,
  Select,
  TextInput,
  Tooltip,
  Group,
} from "@mantine/core";
import ApiService from "../../../services/api";
import fireToast from "../../../helpers/toaster";
import { Dropzone } from "@mantine/dropzone";
import PhoneInputComp from "../../Phone Input/PhoneInputComp";
import {
  getFileBaseName,
  isBlank,
  isValidEmail,
} from "../../../helpers/functions";
import { DatePickerInput } from "@mantine/dates";

const GOVT_ID_NUMBER_NO_SPACES = /^\S*$/;

const AddPatient = () => {
  const { dropdownValues } = useDropdownValues();
  const [openAddPatient, setOpenAddPatient] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dropzoneLoadings, setDropzoneLoadings] = useState({
    photo: false,
    governmentId: false,
  });
  const [states, setStates] = useState([]);
  const [governmentIdFileName, setGovernmentIdFileName] = useState("");

  const patientForm = useForm({
    initialValues: {
      photoUrl: "",
      governmentIdUrl: "",
      idNumber: "",
      title: "",
      sex: "",
      firstName: "",
      lastName: "",
      dateOfBirth: null,
      phoneNumber: "",
      email: "",
      nextOfKin: "",
      address: "",
      country: "",
      state: "",
      city: "",
      bloodGroup: "",
    },
    validate: {
      idNumber: (value) => {
        if (isBlank(value)) return null;
        if (!GOVT_ID_NUMBER_NO_SPACES.test(value)) {
          return "ID number cannot contain spaces";
        }
        return null;
      },
      title: (value) => (isBlank(value) ? "Title is required" : null),
      sex: (value) => (isBlank(value) ? "Sex is required" : null),
      firstName: (value) => (isBlank(value) ? "First name is required" : null),
      lastName: (value) => {
        if (isBlank(value)) return null;
        if (String(value).trim().length < 2) {
          return "Last name must be at least 2 characters";
        }
        return null;
      },
      dateOfBirth: (value) =>
        isBlank(value) ? "Date of birth is required" : null,
      phoneNumber: (value) => {
        if (!value)
          return fireToast(
            "error",
            "Phone number is required",
            "bottom-right",
            true,
          );
        else if (value.length < 10)
          return fireToast(
            "error",
            "Phone number should be at least 10 digits",
            "bottom-right",
            true,
          );
        return null;
      },
      email: (value) => {
        if (isBlank(value)) return null;
        if (!isValidEmail(value)) return "Invalid email";
        return null;
      },
    },
  });

  const resetPatientForm = () => {
    patientForm.reset();
    setGovernmentIdFileName("");
  };

  const closeModal = () => {
    setOpenAddPatient(false);
    resetPatientForm();
  };

  const uploadPhoto = (file) => {
    setDropzoneLoadings((prev) => ({
      ...prev,
      photo: true,
    }));
    const formData = new FormData();
    formData.append("file", file);

    ApiService.uploadSingleFile("patient_profile_image", formData)
      .then((response) => {
        patientForm.setFieldValue("photoUrl", response.data.data.url);
        setDropzoneLoadings((prev) => ({
          ...prev,
          photo: false,
        }));
      })
      .catch((error) => {
        if (error.response) {
          setDropzoneLoadings((prev) => ({
            ...prev,
            photo: false,
          }));
          fireToast("error", error.response.data.message, "bottom-right", true);
        }
      });
  };

  const uploadGovernmentId = (file) => {
    setGovernmentIdFileName(file?.name ?? "");
    setDropzoneLoadings((prev) => ({
      ...prev,
      governmentId: true,
    }));
    const formData = new FormData();
    formData.append("file", file);

    ApiService.uploadSingleFile("patient_gov_id", formData)
      .then((response) => {
        patientForm.setFieldValue("governmentIdUrl", response.data.data.url);
        setDropzoneLoadings((prev) => ({
          ...prev,
          governmentId: false,
        }));
      })
      .catch((error) => {
        if (error.response) {
          setDropzoneLoadings((prev) => ({
            ...prev,
            governmentId: false,
          }));
          fireToast("error", error.response.data.message, "bottom-right", true);
        }
      });
  };

  const submitPatient = (values) => {
    if (!patientForm.validate().hasErrors) {
      setLoading(true);
      ApiService.addPatient(values)
        .then(() => {
          setLoading(false);
          closeModal();
          window.dispatchEvent(new Event("receptionist-patients-refresh"));
        })
        .catch((error) => {
          if (error.response) {
            setLoading(false);
            fireToast(
              "error",
              error.response.data.message,
              "bottom-right",
              true,
            );
          }
        });
    }
  };

  const fetchStates = (country) => {
    if (!country) return;
    setStates(dropdownValues.states.statesByCountry[country].options);
  };

  useEffect(() => {
    const handler = () => setOpenAddPatient(true);
    window.addEventListener(ADD_PATIENT_MODAL_EVENT, handler);
    return () => window.removeEventListener(ADD_PATIENT_MODAL_EVENT, handler);
  }, []);

  return (
    <>
      <PrimaryButton
        text="Create Patient"
        leftSection={<IconUser />}
        variant="outline"
        onClick={() => {
          setOpenAddPatient(true);
        }}
      />

      <Modal
        opened={openAddPatient}
        onClose={closeModal}
        padding="0px"
        withCloseButton={false}
        size="720px"
        radius="md"
        centered
      >
        <div className={styles.add_patient_modal_container}>
          <div className={styles.sticky_header}>
            <div className={styles.heading_container}>
              <p className={styles.heading}>New Patient Registration</p>
              <p className={styles.desc}>
                Capture identity, contact, and address to create a patient
                record.
              </p>
            </div>
            <ActionIcon
              className={styles.close_btn}
              variant="subtle"
              color="gray"
              radius="md"
              size="lg"
              aria-label="Close"
              onClick={closeModal}
            >
              <IconX size={18} />
            </ActionIcon>
          </div>

          <div className={styles.modal_scroll}>
            <section className={styles.section}>
              <div className={styles.section_header}>
                <p className={styles.section_title}>Documents</p>
                <p className={styles.section_hint}>
                  Optional photo and government ID for verification.
                </p>
              </div>
              <div className={styles.docs_row}>
                <div className={styles.photo_block}>
                  <p className={styles.photo_label}>Photo</p>
                  <Dropzone
                    onDrop={(files) => uploadPhoto(files[0])}
                    accept={["image/png", "image/jpg", "image/jpeg"]}
                    maxFiles={1}
                    loading={dropzoneLoadings.photo}
                    className={styles.photo_dropzone}
                  >
                    <div className={styles.photo_drop_inner}>
                      {patientForm.values.photoUrl ? (
                        <img
                          src={patientForm.values.photoUrl}
                          alt="Patient"
                          className={styles.photo_preview}
                        />
                      ) : (
                        <>
                          <span
                            className={styles.photo_placeholder_icon}
                            aria-hidden="true"
                          >
                            <IconCamera size={22} stroke={1.5} />
                          </span>
                          <p className={styles.photo_cta}>
                            <span>Upload</span> photo
                          </p>
                          <p className={styles.photo_formats}>JPG or PNG</p>
                        </>
                      )}
                    </div>
                  </Dropzone>
                </div>

                <div className={styles.gov_block}>
                  <p className={styles.photo_label}>Government ID</p>
                  <Dropzone
                    onDrop={(files) => uploadGovernmentId(files[0])}
                    accept={["application/pdf"]}
                    maxFiles={1}
                    loading={dropzoneLoadings.governmentId}
                    className={styles.gov_dropzone}
                  >
                    <div className={styles.gov_drop_inner}>
                      {patientForm.values.governmentIdUrl ? (
                        <div className={styles.gov_file_row}>
                          <span
                            className={styles.gov_file_icon}
                            aria-hidden="true"
                          >
                            <IconFile size={20} stroke={1.5} />
                          </span>
                          <div className={styles.gov_file_meta}>
                            <p className={styles.gov_file_name}>
                              {governmentIdFileName ||
                                getFileBaseName(
                                  patientForm.values.governmentIdUrl,
                                )}
                            </p>
                            <p className={styles.gov_file_hint}>PDF uploaded</p>
                          </div>
                          <span className={styles.gov_change}>Change</span>
                        </div>
                      ) : (
                        <>
                          <span
                            className={styles.gov_file_icon}
                            aria-hidden="true"
                          >
                            <IconUpload size={18} stroke={1.5} />
                          </span>
                          <p className={styles.gov_empty_title}>
                            Drop PDF or <span>browse</span>
                          </p>
                          <p className={styles.gov_empty_hint}>
                            PDF only · used for identity verification
                          </p>
                        </>
                      )}
                    </div>
                  </Dropzone>
                </div>
              </div>
            </section>

            <section className={styles.section}>
              <div className={styles.section_header}>
                <p className={styles.section_title}>Identity</p>
              </div>
              <div className={styles.section_grid}>
                <Select
                  label="Title"
                  placeholder="Select title"
                  data={dropdownValues?.titles?.options}
                  required
                  size="md"
                  clearable
                  {...patientForm.getInputProps("title")}
                />
                <TextInput
                  label="ID Number"
                  placeholder="Enter ID number"
                  size="md"
                  classNames={{
                    input: styles.id_number_input,
                    wrapper: styles.id_number_wrapper,
                  }}
                  {...patientForm.getInputProps("idNumber")}
                  onKeyDown={(e) => {
                    if (e.key === " ") e.preventDefault();
                  }}
                  onChange={(e) => {
                    patientForm.setFieldValue(
                      "idNumber",
                      e.currentTarget.value.replace(/\s/g, ""),
                    );
                  }}
                />
                <TextInput
                  label="First Name"
                  placeholder="Enter first name"
                  required
                  size="md"
                  {...patientForm.getInputProps("firstName")}
                />
                <TextInput
                  label="Last Name"
                  placeholder="Enter last name"
                  size="md"
                  {...patientForm.getInputProps("lastName")}
                />
                <Select
                  label="Gender"
                  placeholder="Select gender"
                  data={dropdownValues?.genders?.options}
                  required
                  size="md"
                  clearable
                  {...patientForm.getInputProps("sex")}
                />
                <DatePickerInput
                  label="Date of Birth"
                  placeholder="Select date of birth"
                  required
                  size="md"
                  maxDate={new Date()}
                  valueFormat="DD MMM, YYYY"
                  rightSection={<IconCalendar size={18} />}
                  {...patientForm.getInputProps("dateOfBirth")}
                />
                <Select
                  label="Blood Group"
                  placeholder="Select blood group"
                  data={dropdownValues?.bloodGroups?.options}
                  size="md"
                  clearable
                  {...patientForm.getInputProps("bloodGroup")}
                />
                <TextInput
                  label={
                    <Group gap={6} wrap="nowrap" align="center">
                      <span>Next of Kin</span>
                      <Tooltip
                        label="the person who should be contacted in case of an emergency, illness, injury, or death"
                        multiline
                        w={260}
                        withArrow
                        position="top"
                      >
                        <ActionIcon
                          variant="transparent"
                          size="xs"
                          color="gray"
                          aria-label="Next of kin info"
                          style={{ cursor: "help" }}
                        >
                          <IconInfoCircle size={14} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  }
                  placeholder="Enter next of kin"
                  size="md"
                  {...patientForm.getInputProps("nextOfKin")}
                />
              </div>
            </section>

            <section className={styles.section}>
              <div className={styles.section_header}>
                <p className={styles.section_title}>Contact</p>
              </div>
              <div className={styles.section_grid}>
                <PhoneInputComp
                  label="Phone Number"
                  placeholder="Enter phone number"
                  value={patientForm.values.phoneNumber}
                  onChange={(value) => {
                    patientForm.setFieldValue("phoneNumber", value);
                  }}
                  required={true}
                />
                <TextInput
                  label="Email"
                  placeholder="Enter email"
                  size="md"
                  {...patientForm.getInputProps("email")}
                />
              </div>
            </section>

            <section className={styles.section}>
              <div className={styles.section_header}>
                <p className={styles.section_title}>Address</p>
              </div>
              <div className={styles.section_grid}>
                <TextInput
                  className={styles.span_2}
                  label="Street address"
                  placeholder="Enter address"
                  size="md"
                  {...patientForm.getInputProps("address")}
                />
                <Select
                  label="Country"
                  placeholder="Select country"
                  data={dropdownValues?.countries?.options}
                  size="md"
                  searchable
                  clearable
                  {...patientForm.getInputProps("country")}
                  onChange={(value) => {
                    patientForm.setFieldValue("country", value);
                    fetchStates(value);
                    patientForm.setFieldValue("state", null);
                  }}
                />
                <Select
                  label="State"
                  placeholder="Select state"
                  data={states}
                  size="md"
                  searchable
                  clearable
                  {...patientForm.getInputProps("state")}
                />
                <TextInput
                  label="City"
                  placeholder="Enter city"
                  size="md"
                  {...patientForm.getInputProps("city")}
                />
              </div>
            </section>
          </div>

          <div className={styles.buttons_container}>
            <PrimaryButton
              text="Cancel"
              variant="outline"
              onClick={closeModal}
            />
            <PrimaryButton
              text="Create Patient"
              loading={loading}
              type="submit"
              onClick={() => {
                submitPatient(patientForm.values);
              }}
            />
          </div>
        </div>
      </Modal>
    </>
  );
};

export default AddPatient;
